/**
 * Screen Store
 *
 * Zustand store for ESP32 screen management.
 * Handles CRUD operations for reusable screen definitions.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useAuthStore } from '@/stores/auth/auth-store';
import { DeviceVariantId, ScreenResolution } from '@/types/workflow-nodes';
import { workflowFetch } from '@/services/api/workflowApi';
import { buildWorkflowUserApiRoute } from '@/utils/workflow-utils';

// Screen element types for ESP32 display
export type ScreenElementType =
  | 'text'
  | 'button'
  | 'rect'
  | 'header'
  | 'gauge'
  | 'numeric_stepper';

// Touch action types
export type TouchActionType =
  | 'navigate'
  | 'set_variable'
  | 'increment'
  | 'decrement'
  | 'toggle'
  | 'publish_mqtt';

// Touch action configuration
export interface TouchAction {
  type: TouchActionType;
  target?: string; // Screen ID for navigate, variable name for set_variable
  value?: string | number | boolean;
  topic?: string; // For MQTT publish
  min?: number;
  max?: number;
  step?: number;
}

// Screen element definition
export interface ScreenElement {
  id: string;
  type: ScreenElementType;
  x: number; // 0-319
  y: number; // 0-239
  width: number;
  height: number;
  zIndex: number;
  properties: Record<string, unknown>;
  touchAction?: TouchAction;
}

// Screen definition (what's rendered on ESP32)
export interface ScreenDefinition {
  backgroundColor: string;
  elements: ScreenElement[];
}

// Full screen model
export interface Screen {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  definition: ScreenDefinition;
  resolution: {
    width: number;
    height: number;
  };
  tags?: string[];
  owner_type: 'User' | 'Organization';
  owner_id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// Default resolution for ESP32 displays
const DEFAULT_RESOLUTION: ScreenResolution = { width: 320, height: 240 };

// Variant resolution mapping (matches backend DEVICE_VARIANTS)
const VARIANT_RESOLUTIONS: Partial<Record<DeviceVariantId, ScreenResolution>> = {
  [DeviceVariantId.ESP32_2_8_ILI9341]: { width: 320, height: 240 },
  [DeviceVariantId.ESP32S3_WAVESHARE_7IN]: { width: 800, height: 480 },
};

const getVariantResolution = (variantId: DeviceVariantId | null): ScreenResolution | null => {
  if (!variantId) {
    return null;
  }
  return VARIANT_RESOLUTIONS[variantId] ?? null;
};

const getCanvasResolutionForVariant = (variantId: DeviceVariantId | null): ScreenResolution => (
  getVariantResolution(variantId) ?? DEFAULT_RESOLUTION
);

interface ScreenState {
  // Data
  screens: Screen[];
  selectedScreen: Screen | null;

  // Builder state
  isBuilderOpen: boolean;
  currentScreen: Screen | null;
  selectedElement: ScreenElement | null;

  // Workflow context
  currentWorkflowId: string | null;
  currentVariantId: DeviceVariantId | null;
  canvasResolution: ScreenResolution;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  screensLoaded: boolean;
  error: string | null;

  // Resolution actions
  setTargetVariant: (variantId: DeviceVariantId | null) => void;
  setWorkflowContext: (workflowId: string | null, variantId: DeviceVariantId | null) => void;
  getCanvasResolution: () => ScreenResolution;

  // Actions
  fetchScreens: () => Promise<void>;
  getScreen: (screenId: string) => Promise<Screen | null>;
  createScreen: (data: Partial<Screen>) => Promise<Screen | null>;
  updateScreen: (screenId: string, data: Partial<Screen>) => Promise<Screen | null>;
  deleteScreen: (screenId: string) => Promise<boolean>;
  duplicateScreen: (screenId: string, newName?: string) => Promise<Screen | null>;

  // Builder actions
  openBuilder: (screen?: Screen) => void;
  closeBuilder: () => void;
  setCurrentScreen: (screen: Screen | null) => void;
  updateCurrentScreen: (updates: Partial<Screen>) => void;

  // Element actions
  addElement: (element: ScreenElement) => void;
  updateElement: (elementId: string, updates: Partial<ScreenElement>) => void;
  deleteElement: (elementId: string) => void;
  selectElement: (element: ScreenElement | null) => void;
  moveElement: (elementId: string, x: number, y: number) => void;
  resizeElement: (elementId: string, width: number, height: number) => void;

  // Save
  saveCurrentScreen: () => Promise<Screen | null>;
}

// Helper to get user ID
const getUserId = () => {
  const user = useAuthStore.getState().user;
  return user?._id || user?.id;
};

// Create a new empty screen with specified resolution
const createEmptyScreen = (resolution: ScreenResolution = DEFAULT_RESOLUTION): Screen => ({
  id: '',
  name: 'New Screen',
  description: '',
  definition: {
    backgroundColor: '#000000',
    elements: [],
  },
  resolution: {
    width: resolution.width,
    height: resolution.height,
  },
  tags: [],
  owner_type: 'User',
  owner_id: '',
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const useScreenStore = create<ScreenState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      screens: [],
      selectedScreen: null,
      isBuilderOpen: false,
      currentScreen: null,
      selectedElement: null,
      currentWorkflowId: null,
      currentVariantId: null,
      canvasResolution: DEFAULT_RESOLUTION,
      isLoading: false,
      isSaving: false,
      screensLoaded: false,
      error: null,

      // Resolution actions
      setTargetVariant: (variantId: DeviceVariantId | null) => {
        const resolution = getCanvasResolutionForVariant(variantId);
        set({
          currentVariantId: variantId,
          canvasResolution: resolution,
        });
        // Update current screen resolution if editing
        const { currentScreen } = get();
        if (currentScreen) {
          set((state) => {
            if (state.currentScreen) {
              state.currentScreen.resolution = { ...resolution };
            }
          });
        }
      },

      /**
       * Set current workflow context
       * This syncs the workflow ID and variant for screen filtering and creation
       */
      setWorkflowContext: (workflowId: string | null, variantId: DeviceVariantId | null) => {
        const resolution = getCanvasResolutionForVariant(variantId);

        console.log('[ScreenStore] setWorkflowContext called:', {
          workflowId,
          variantId,
          resolution,
          VARIANT_RESOLUTIONS
        });

        set({
          currentWorkflowId: workflowId,
          currentVariantId: variantId,
          canvasResolution: resolution,
          screensLoaded: false,  // Reset to allow re-fetch with new workflow_id
        });

        // If there's a current screen being edited, update its resolution
        const { currentScreen } = get();
        if (currentScreen) {
          set((state) => {
            if (state.currentScreen) {
              state.currentScreen.resolution = { ...resolution };
            }
          });
        }
      },

      getCanvasResolution: () => {
        return get().canvasResolution;
      },

      // Fetch all screens
      fetchScreens: async () => {
        const { isLoading, screensLoaded, currentWorkflowId } = get();

        // Guard: Don't fetch if already loading or already loaded
        if (isLoading || screensLoaded) {
          return;
        }

        const userId = getUserId();
        if (!userId) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          // ADD workflow_id query parameter
          const queryParams = new URLSearchParams();
          if (currentWorkflowId) {
            queryParams.set('workflow_id', currentWorkflowId);
          }
          const queryString = queryParams.toString();
          const url = `${buildWorkflowUserApiRoute('screens', userId)}${queryString ? `?${queryString}` : ''}`;

          const response = await workflowFetch(url, {
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch screens: ${response.statusText}`);
          }

          const result = await response.json();
          const screens = result.data?.screens || [];

          set({
            screens: screens.map((s: Screen) => ({ ...s, id: s._id || s.id })),
            isLoading: false,
            screensLoaded: true,
          });
        } catch (error) {
          console.error('Failed to fetch screens:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch screens',
            isLoading: false,
          });
        }
      },

      // Get single screen
      getScreen: async (screenId: string) => {
        const userId = getUserId();
        if (!userId) return null;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`screens/${screenId}`, userId),
            {
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) return null;

          const result = await response.json();
          return { ...result.data, id: result.data._id || result.data.id };
        } catch (error) {
          console.error('Failed to get screen:', error);
          return null;
        }
      },

      // Create new screen
      createScreen: async (data: Partial<Screen>) => {
        const userId = getUserId();
        if (!userId) {
          set({ error: 'User not authenticated' });
          return null;
        }

        const { currentWorkflowId, currentVariantId, canvasResolution } = get();

        set({ isSaving: true, error: null });

        try {
          // DEFENSIVE: Ensure we work with a local copy, never mutate store objects directly.
          const resolution: ScreenResolution = data.resolution
            ? { width: data.resolution.width, height: data.resolution.height }
            : { width: canvasResolution.width, height: canvasResolution.height };

          // DEFENSIVE: Validate resolution matches expected variant
          const expectedResolution = getVariantResolution(currentVariantId);
          if (expectedResolution) {
            if (resolution.width !== expectedResolution.width ||
                resolution.height !== expectedResolution.height) {
              console.warn('[ScreenStore] Resolution mismatch detected, using expected resolution', {
                got: resolution,
                expected: expectedResolution,
                variant: currentVariantId
              });
              // Force correct resolution
              resolution.width = expectedResolution.width;
              resolution.height = expectedResolution.height;
            }
          }

          const payload = {
            ...data,
            resolution,  // Now guaranteed to be correct
            workflow_id: currentWorkflowId,  // ADD THIS
          };

          const response = await workflowFetch(
            buildWorkflowUserApiRoute('screens', userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create screen');
          }

          const result = await response.json();
          const newScreen = { ...result.data, id: result.data._id || result.data.id };

          set((state) => {
            state.screens.push(newScreen);
            state.isSaving = false;
          });

          return newScreen;
        } catch (error) {
          console.error('Failed to create screen:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create screen',
            isSaving: false,
          });
          return null;
        }
      },

      // Update screen
      updateScreen: async (screenId: string, data: Partial<Screen>) => {
        const userId = getUserId();
        if (!userId) return null;

        set({ isSaving: true, error: null });

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`screens/${screenId}`, userId),
            {
            method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            throw new Error('Failed to update screen');
          }

          const result = await response.json();
          const updatedScreen = { ...result.data, id: result.data._id || result.data.id };

          set((state) => {
            const index = state.screens.findIndex((s) => s.id === screenId);
            if (index !== -1) {
              state.screens[index] = updatedScreen;
            }
            state.isSaving = false;
          });

          return updatedScreen;
        } catch (error) {
          console.error('Failed to update screen:', error);
          set({ isSaving: false });
          return null;
        }
      },

      // Delete screen
      deleteScreen: async (screenId: string) => {
        const userId = getUserId();
        if (!userId) return false;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`screens/${screenId}`, userId),
            {
            method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
            },
          );

          if (!response.ok) {
            throw new Error('Failed to delete screen');
          }

          set((state) => {
            state.screens = state.screens.filter((s) => s.id !== screenId);
          });

          return true;
        } catch (error) {
          console.error('Failed to delete screen:', error);
          return false;
        }
      },

      // Duplicate screen
      duplicateScreen: async (screenId: string, newName?: string) => {
        const userId = getUserId();
        if (!userId) return null;

        try {
          const response = await workflowFetch(
            buildWorkflowUserApiRoute(`screens/${screenId}/duplicate`, userId),
            {
            method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newName }),
            },
          );

          if (!response.ok) {
            throw new Error('Failed to duplicate screen');
          }

          const result = await response.json();
          const newScreen = { ...result.data, id: result.data._id || result.data.id };

          set((state) => {
            state.screens.push(newScreen);
          });

          return newScreen;
        } catch (error) {
          console.error('Failed to duplicate screen:', error);
          return null;
        }
      },

      // Builder actions
      openBuilder: (screen?: Screen) => {
        const { canvasResolution, currentVariantId } = get();

        console.log('[ScreenStore] openBuilder called:', {
          hasScreen: !!screen,
          canvasResolution,
          currentVariantId,
        });

        // DEFENSIVE: Verify canvas resolution matches current variant
        const expectedResolution = getVariantResolution(currentVariantId);
        if (expectedResolution) {
          if (canvasResolution.width !== expectedResolution.width ||
              canvasResolution.height !== expectedResolution.height) {
            console.warn('[ScreenStore] Canvas resolution out of sync with variant, correcting', {
              canvasResolution,
              expectedResolution,
              variant: currentVariantId
            });
            // Sync resolution immediately
            set({ canvasResolution: expectedResolution });
          }
        }

        const finalResolution = get().canvasResolution;
        console.log('[ScreenStore] Opening builder with resolution:', finalResolution);

        set({
          isBuilderOpen: true,
          currentScreen: screen || createEmptyScreen(finalResolution),  // Use synced resolution
          selectedElement: null,
        });
      },

      closeBuilder: () => {
        set({
          isBuilderOpen: false,
          currentScreen: null,
          selectedElement: null,
        });
      },

      setCurrentScreen: (screen: Screen | null) => {
        set({ currentScreen: screen, selectedElement: null });
      },

      updateCurrentScreen: (updates: Partial<Screen>) => {
        set((state) => {
          if (state.currentScreen) {
            Object.assign(state.currentScreen, updates);
          }
        });
      },

      // Element actions
      addElement: (element: ScreenElement) => {
        set((state) => {
          if (state.currentScreen) {
            state.currentScreen.definition.elements.push(element);
            state.selectedElement = element;
          }
        });
      },

      updateElement: (elementId: string, updates: Partial<ScreenElement>) => {
        set((state) => {
          if (state.currentScreen) {
            const elementIndex = state.currentScreen.definition.elements.findIndex((e) => e.id === elementId);
            if (elementIndex !== -1) {
              const element = state.currentScreen.definition.elements[elementIndex];
              // Merge properties if both exist
              const mergedProperties = updates.properties
                ? { ...element.properties, ...updates.properties }
                : element.properties;

              // Create new element with updates
              const updatedElement: ScreenElement = {
                ...element,
                ...updates,
                properties: mergedProperties,
              };

              // Update in array
              state.currentScreen.definition.elements[elementIndex] = updatedElement;

              // Update selectedElement if it's the same element
              if (state.selectedElement?.id === elementId) {
                state.selectedElement = updatedElement;
              }
            }
          }
        });
      },

      deleteElement: (elementId: string) => {
        set((state) => {
          if (state.currentScreen) {
            state.currentScreen.definition.elements = state.currentScreen.definition.elements.filter(
              (e) => e.id !== elementId
            );
            if (state.selectedElement?.id === elementId) {
              state.selectedElement = null;
            }
          }
        });
      },

      selectElement: (element: ScreenElement | null) => {
        set({ selectedElement: element });
      },

      moveElement: (elementId: string, x: number, y: number) => {
        const { canvasResolution } = get();
        set((state) => {
          if (state.currentScreen) {
            const element = state.currentScreen.definition.elements.find((e) => e.id === elementId);
            if (element) {
              // Clamp to canvas bounds using current resolution
              element.x = Math.max(0, Math.min(x, canvasResolution.width - element.width));
              element.y = Math.max(0, Math.min(y, canvasResolution.height - element.height));
              if (state.selectedElement?.id === elementId) {
                state.selectedElement.x = element.x;
                state.selectedElement.y = element.y;
              }
            }
          }
        });
      },

      resizeElement: (elementId: string, width: number, height: number) => {
        const { canvasResolution } = get();
        set((state) => {
          if (state.currentScreen) {
            const element = state.currentScreen.definition.elements.find((e) => e.id === elementId);
            if (element) {
              // Ensure element stays within canvas using current resolution
              element.width = Math.max(10, Math.min(width, canvasResolution.width - element.x));
              element.height = Math.max(10, Math.min(height, canvasResolution.height - element.y));
              if (state.selectedElement?.id === elementId) {
                state.selectedElement.width = element.width;
                state.selectedElement.height = element.height;
              }
            }
          }
        });
      },

      // Save current screen
      saveCurrentScreen: async () => {
        const { currentScreen } = get();
        if (!currentScreen) return null;

        if (currentScreen.id) {
          // Update existing
          return get().updateScreen(currentScreen.id, {
            name: currentScreen.name,
            description: currentScreen.description,
            definition: currentScreen.definition,
            resolution: currentScreen.resolution,
            tags: currentScreen.tags,
          });
        } else {
          // Create new
          const newScreen = await get().createScreen({
            name: currentScreen.name,
            description: currentScreen.description,
            definition: currentScreen.definition,
            resolution: currentScreen.resolution,
            tags: currentScreen.tags,
          });
          if (newScreen) {
            set({ currentScreen: newScreen });
          }
          return newScreen;
        }
      },
    })),
    { name: 'screen-store' }
  )
);
